/*
 * The AI plane — SHARED by every base-ui app.
 *
 * Every Vitesse product UI gets the same AI capability (text and voice, one agent, one log). What
 * differs per UI is TOOLS, SUBAGENTS, and PROMPTS — and none of that lives here: the handshake sends
 * a `platform` string and the server resolves the roster for it. So there is no per-app AI code to
 * fork, and an app that wants AI mounts this rather than reimplementing it.
 *
 * The one genuinely per-app piece is the worker ENTRY (`Comlink.expose(createAiApi(AI_URL))`), which
 * has to be a real file in the app so its bundler can resolve the worker URL, and which supplies the
 * app's own origin-derived backend URL.
 */
export { createAiApi } from './api';
export { foldTranscript } from './transcript-fold';
// The data layer, exported on its own: a redesigned list widget should reuse this rather than
// re-implement paging, the count and the cache.
export { ConversationsStore } from './conversations-store';
export { conversationEvents, conversationsDeleted, onConversationChange } from './conversation-events';
// The list itself, as an element. Registered on import so a host that renders the tag gets the
// definition without having to know the module path.
export { VitesseConversationList, fmtWhen, fmtCost, conversationSubtitle } from './conversation-list';
// The model roster — shared so the composer in the rail and the dashboard's pickers offer the same
// models. A second copy would drift, and which one a user saw would depend on the window.
export { REALTIME_MODELS, TEXT_MODELS, IMAGE_MODELS, loadModelCatalog, catalogEvents, findModel } from './model-catalog';
export { pickGreeting, partOfDay, greetingSeed, GREETINGS } from './greeting';
export { screenFiles, mediaAccepted, fmtSize, ACCEPTED_MEDIA, ACCEPT_ATTR, FILE_MAX_BYTES } from './attachments';
export { VitesseAiTools } from './tools-menu';
// How a tool is NAMED to a person. Shared so the permissions screen and an inline "running…" chip
// in a chat cannot call the same capability two different things.
export { toolDisplayName, toolGroupName } from './toolname';
// The capabilities every app is expected to answer, declared once so they govern as one.
export { PLATFORM_TOOLS, PLATFORM_PREFIX, implementPlatformTool, implementDomPlatformTools, isPlatformTool } from './platform-tools';
// The client-tool registry. Exported because a UI declaring its OWN capabilities is the point of it
// — the mechanism was built and tested and then reachable only from inside this package, so every
// surface had the three central platform tools and no way to add one of its own.
export { registerClientTools, clientToolDeclarations, dispatchClientTool, isClientTool } from './tools';
export { registerGatewayTools } from './gateway-tools';
export { readScreen, readSelection, holdSelection, releaseSelection, watchSelection } from './screen';
export { openMic, openSpeaker, toPcm16, fromPcm16, toBase64, fromBase64 } from './voice-audio';
export { startVoiceCall } from './voice-call';
export { makeBrowserStep } from './step';
export { runTree, driveToCompletion, resumeAbandonedWork } from './driver';
export { RunTree, treeRunsOf } from './run-tree';
import './run-tree-view';
export { RunTranscript } from './run-transcript';
export { turnStyles, renderTurn, renderTool, renderReturn, sequenceOf } from './turn-view';
import { setPlatform } from './api';
import { configureDriver } from './driver';
import { configureLive } from './live';
/**
 * Wire the AI plane to this app's origin and session.
 *
 * ONE call rather than one per module: the driver, the worker api and the live channel all need the
 * same two facts, and configuring them separately is how an app ends up with a driver pointed at
 * one backend and a session pointed at another. The live half is what makes config changes reach
 * every open surface without a reload — it rides along here precisely so no app can have the plane
 * without it.
 */
export function configureAiPlane(aiUrl, token, platform = 'ai') {
    configureDriver(aiUrl, token);
    configureLive(aiUrl, token);
    // The surface's name rides every transcript write from here on (api.ts): the archive stamps a
    // conversation's platform from this, and the default stamped every app's threads as the console's.
    setPlatform(platform);
}
export { live, LiveTopic } from './live';
export { AiClient } from './client';
export { AiManager, aiConfigEvents } from './manager';
export { Conversation, memoryTranscriptStore, durableTranscriptStore } from './conversation';
import './ask-user'; // registers the built-in ask_user client tool (available on every surface)
export { pendingAsk, onAskChange, answerAsk, cancelAsk } from './ask-user';
import './panel'; // registers <vitesse-ai-panel>
import './chat'; // registers <vitesse-ai-chat>
export { toolsForRole, roleAllows } from './roletools';
export { DEFAULT_SPECIALIST, specialistLabel } from './specialists';
export { registerWorkCard, workCardFor, registerFenceRenderer, fenceRendererFor, fenceToolId, renderXml, tokenizeXml, renderWorkList, partialString } from './work-card';
export { liveThread, unarchived } from './live-thread';
/** The transcript's Markdown renderer, for surfaces that show what a model wrote outside a chat — a
 *  note tile a designer filled in. */
export { renderMarkdown } from './markdown';
//# sourceMappingURL=index.js.map